// components/accessDenied.jsx
import React, { useContext, useMemo } from 'react';
import { Result, Button, Card, Typography, Space, Tag } from 'antd';
import {
  LockOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../common/constants';
import { getRoleLabel, getRoleColor } from '../common/auth/roleLabels';

const { Text, Title } = Typography;

// Rôles ayant accès complet
const FULL_ACCESS_ROLES = ['superAdmin', 'directeur'];

// Configuration des routes avec leurs permissions
const ROUTE_PERMISSIONS = [
  { path: '/dashboard/clients', perm: 'clients:read', label: 'Clients' },
  { path: '/dashboard/contrats', perm: 'contrats:read', label: 'Contrats' },
  { path: '/dashboard/reglements', perm: 'reglements:read', label: 'Règlements' },
  { path: '/dashboard/impayes', perm: 'impayes:read', label: 'Impayés' },
  { path: '/dashboard/sinistres', perm: 'sinistres:read', label: 'Sinistres' },
  { path: '/dashboard/bilans', perm: 'bilans:read', label: 'Bilans & États' },
  { path: '/dashboard/compagnies', perm: 'compagnies:read', label: 'Compagnies' },
  { path: '/dashboard/types-assurance', perm: 'typesAssurance:read', label: "Types d'assurance" },
  { path: '/dashboard/agences', perm: 'agences:read', label: 'Agences' },
  { path: '/dashboard/parametrages', perm: 'parametrages:read', label: 'Paramétrages' },
  { path: '/dashboard/gestion-admins', perm: 'users:read', label: 'Administrateurs' },
  { path: '/dashboard/roles', perm: 'roles:read', label: 'Rôles' },
  { path: '/dashboard/audit-log', perm: 'audit:read', label: "Journal d'audit" },
  { path: '/dashboard', perm: 'dashboard:view', label: 'Tableau de bord' },
];

const AccessDenied = ({ requiredPerm }) => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // Trouver la première page accessible pour l'utilisateur
  const firstAccessiblePage = useMemo(() => {
    if (!currentUser) return null;

    // Si l'utilisateur a un rôle avec accès complet
    if (FULL_ACCESS_ROLES.includes(currentUser.role)) {
      return { path: '/dashboard', label: 'Tableau de bord' };
    }

    const perms = new Set(currentUser.permissions || []);

    for (const route of ROUTE_PERMISSIONS) {
      if (route.anyOf) {
        if (route.anyOf.some(p => perms.has(p))) {
          return route;
        }
      } else if (route.perm) {
        if (perms.has(route.perm)) {
          return route;
        }
      }
    }

    return null;
  }, [currentUser]);

  const handleGoToAccessible = () => {
    if (firstAccessiblePage) {
      navigate(firstAccessiblePage.path);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)',
        padding: 24,
      }}
    >
      <Card
        style={{
          maxWidth: 500,
          width: '100%',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Header gradient */}
        <div
          style={{
            background: 'linear-gradient(135deg, #C8102E 0%, #1A2B5F 100%)',
            padding: '40px 24px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <LockOutlined style={{ fontSize: 40, color: '#fff' }} />
          </div>
          <Title level={3} style={{ color: '#fff', margin: 0 }}>
            Accès Restreint
          </Title>
        </div>

        {/* Body */}
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Text style={{ fontSize: 16, color: '#595959', display: 'block', marginBottom: 24 }}>
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </Text>

          {requiredPerm && (
            <div style={{ marginBottom: 24 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Permission requise :
              </Text>
              <div style={{ marginTop: 8 }}>
                <Tag color="red" icon={<SafetyCertificateOutlined />} style={{ padding: '4px 12px' }}>
                  {requiredPerm}
                </Tag>
              </div>
            </div>
          )}

          {currentUser && (
            <Card
              size="small"
              style={{
                backgroundColor: '#fafafa',
                marginBottom: 24,
                borderRadius: 8,
              }}
            >
              <Space direction="vertical" size={4}>
                <Text strong>{currentUser.firstName} {currentUser.lastName}</Text>
                <Tag color={getRoleColor(currentUser.role)}>{getRoleLabel(currentUser.role)}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {currentUser.permissions?.length || 0} permission(s) attribuée(s)
                </Text>
              </Space>
            </Card>
          )}

          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {firstAccessiblePage && (
              <Button
                type="primary"
                size="large"
                icon={<HomeOutlined />}
                onClick={handleGoToAccessible}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg, #1A2B5F 0%, #2A3E7A 100%)',
                  border: 'none',
                }}
              >
                Aller à {firstAccessiblePage.label}
              </Button>
            )}
            <Button
              size="large"
              icon={<ArrowLeftOutlined />}
              onClick={handleGoBack}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 8,
              }}
            >
              Retour
            </Button>
          </Space>

          <div style={{ marginTop: 24 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Si vous pensez qu'il s'agit d'une erreur, contactez votre administrateur.
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AccessDenied;
